/**
 * KraftStories Analytics Events
 * Centralized dataLayer event dispatcher for GA4 via Google Tag Manager.
 * Listens to existing theme events and pushes GA4-formatted ecommerce
 * and interaction events to window.dataLayer.
 */

(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  function pushEvent(eventName, data) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(Object.assign({ event: eventName }, data));
  }

  function pushNonEcommerceEvent(eventName, data) {
    window.dataLayer.push(Object.assign({ event: eventName }, data));
  }

  function getShopCurrency() {
    return window.__kraft_analytics_currency || 'USD';
  }

  /**
   * Build a GA4 items array entry from a cart line item response object
   * (as returned by /cart/add.js or /cart.js).
   */
  function buildItemFromCartResponse(item) {
    return {
      item_id: String(item.product_id),
      item_name: item.product_title || item.title,
      item_brand: item.vendor || '',
      item_category: item.product_type || '',
      item_variant: item.variant_title || '',
      price: (item.final_price || item.price || 0) / 100,
      quantity: item.quantity || 1,
    };
  }

  /**
   * Build a GA4 items entry from product data- attributes on a DOM element.
   */
  function buildItemFromElement(el) {
    return {
      item_id: el.dataset.productId || '',
      item_name: el.dataset.productName || '',
      item_brand: el.dataset.productBrand || '',
      item_category: el.dataset.productCategory || '',
      item_variant: el.dataset.productVariant || '',
      price: parseFloat(el.dataset.productPrice) || 0,
      quantity: 1,
    };
  }

  // ─── ADD TO CART ──────────────────────────────────────────────────

  /**
   * Handles the theme:cart:add custom event dispatched by product-form-drawer.js.
   * The event.detail.data is the /cart/add.js JSON response.
   */
  function handleCartAdd(event) {
    var data = event.detail && event.detail.data;
    if (!data) return;

    var items = [];
    if (Array.isArray(data.items)) {
      data.items.forEach(function (item) {
        items.push(buildItemFromCartResponse(item));
      });
    } else {
      items.push(buildItemFromCartResponse(data));
    }

    var totalValue = items.reduce(function (sum, i) {
      return sum + i.price * i.quantity;
    }, 0);

    pushEvent('add_to_cart', {
      ecommerce: {
        currency: getShopCurrency(),
        value: totalValue,
        items: items,
      },
    });
  }

  document.addEventListener('theme:cart:add', handleCartAdd);

  // Also capture the module-based CartAddEvent (fires as 'cart:update' with source info)
  document.addEventListener('cart:update', function (event) {
    var detail = event.detail || {};
    if (detail.source === 'product-form-component' || detail.source === 'quick-add') {
      // Already handled by theme:cart:add in most cases, but capture quick-add
      // Avoid duplicates by checking if theme:cart:add already fired
    }
  });

  // ─── REMOVE FROM CART ─────────────────────────────────────────────

  /**
   * Intercept clicks on cart item remove buttons and quantity decrease buttons.
   * We fetch the cart before the action to compare.
   */
  (function () {
    var preActionCart = null;

    function capturePreActionCart() {
      return fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { preActionCart = cart; })
        .catch(function () { preActionCart = null; });
    }

    document.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('.cart-items__remove');
      if (!removeBtn) return;

      capturePreActionCart();

      setTimeout(function () {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (newCart) {
            if (!preActionCart) return;

            var oldIds = {};
            preActionCart.items.forEach(function (item) {
              oldIds[item.key] = item;
            });

            var newIds = {};
            newCart.items.forEach(function (item) {
              newIds[item.key] = item;
            });

            Object.keys(oldIds).forEach(function (key) {
              if (!newIds[key]) {
                var removed = oldIds[key];
                pushEvent('remove_from_cart', {
                  ecommerce: {
                    currency: getShopCurrency(),
                    value: (removed.final_price / 100) * removed.quantity,
                    items: [buildItemFromCartResponse(removed)],
                  },
                });
              }
            });

            preActionCart = null;
          })
          .catch(function () { preActionCart = null; });
      }, 800);
    });
  })();

  // ─── VIEW CART (drawer open) ──────────────────────────────────────

  (function () {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'open') {
          var dialog = m.target;
          if (dialog.hasAttribute('open') && dialog.closest('cart-drawer-component')) {
            fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                if (!cart.items || cart.items.length === 0) return;

                var items = cart.items.map(function (item) {
                  return buildItemFromCartResponse(item);
                });

                var totalValue = items.reduce(function (sum, i) {
                  return sum + i.price * i.quantity;
                }, 0);

                pushEvent('view_cart', {
                  ecommerce: {
                    currency: getShopCurrency(),
                    value: totalValue,
                    items: items,
                  },
                });
              })
              .catch(function () {});
          }
        }
      });
    });

    function observeCartDrawer() {
      var drawerDialog = document.querySelector('cart-drawer-component dialog');
      if (drawerDialog) {
        observer.observe(drawerDialog, { attributes: true, attributeFilter: ['open'] });
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeCartDrawer);
    } else {
      observeCartDrawer();
    }
  })();

  // ─── SELECT ITEM (product card click) ─────────────────────────────

  document.addEventListener('click', function (e) {
    var card = e.target.closest('[data-analytics-product]');
    if (!card) return;

    var item = buildItemFromElement(card);
    if (!item.item_id) return;

    pushEvent('select_item', {
      ecommerce: {
        items: [Object.assign({}, item, {
          item_list_id: card.dataset.listId || '',
          item_list_name: card.dataset.listName || '',
          index: parseInt(card.dataset.productIndex, 10) || 0,
        })],
      },
    });
  });

  // ─── BEGIN CHECKOUT ───────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var checkoutBtn = e.target.closest('#checkout, [name="checkout"]');
    if (!checkoutBtn) return;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) return;

        var items = cart.items.map(function (item) {
          return buildItemFromCartResponse(item);
        });

        var totalValue = items.reduce(function (sum, i) {
          return sum + i.price * i.quantity;
        }, 0);

        pushEvent('begin_checkout', {
          ecommerce: {
            currency: getShopCurrency(),
            value: totalValue,
            items: items,
          },
        });
      })
      .catch(function () {});
  });

  // ─── SEARCH ───────────────────────────────────────────────────────

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.getAttribute('action') !== '/search') return;

    var input = form.querySelector('input[name="q"]');
    if (!input || !input.value.trim()) return;

    pushNonEcommerceEvent('search', {
      search_term: input.value.trim(),
    });
  });

  // ─── SIGN UP (newsletter email signup) ────────────────────────────

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form) return;

    var isEmailSignup =
      form.classList.contains('email-signup__form') ||
      form.closest('email-signup-block');
    if (!isEmailSignup) return;

    var emailInput = form.querySelector('input[name="contact[email]"]');
    if (!emailInput || !emailInput.value.trim()) return;

    pushNonEcommerceEvent('sign_up', {
      method: 'newsletter',
    });
  });

  // ─── MODAL OPEN ───────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-modal-trigger]');
    if (!trigger) return;

    var modalId = trigger.getAttribute('data-modal-trigger');
    pushNonEcommerceEvent('modal_open', {
      modal_id: modalId,
    });
  });

  // ─── LOCATION CHANGE ─────────────────────────────────────────────

  document.addEventListener('locationChange', function (e) {
    var detail = e.detail || {};
    pushNonEcommerceEvent('location_change', {
      location: detail.location || '',
      location_name: detail.locationName || '',
    });
  });

  // ─── EXPOSE CURRENCY FOR JS-ONLY CONTEXTS ────────────────────────

  if (typeof Theme !== 'undefined' && Theme.routes) {
    // Currency is set by gtm-datalayer.liquid, but provide a fallback
  }
})();
